import { AlertErrorCircle, ChevronRight } from '@/assets/icons'
import { RootState } from '@/store/store'
import { useAppSelector } from '@/store/store-hooks'
import { selectAgentV2FollowUpQuestions } from '@/agent-v2/conversations/store/selectors'
import { InfiniteLoader } from '@/components/animations/InfiniteLoader'

export function AgentV2FollowUpQuestions({ assistantMessageId, onQuestionClick }: { assistantMessageId: string; onQuestionClick: (question: string) => void }) {
  const followUpQuestionsState = useAppSelector((state: RootState) => selectAgentV2FollowUpQuestions(state, { assistantMessageId }))

  if (!followUpQuestionsState) return null

  const questions = followUpQuestionsState.questions.slice(0, 3)
  const hasQuestions = questions.length > 0

  return (
    <div className="mt-3 overflow-clip rounded border border-brand-neutral-200 bg-brand-neutral-50">
      <div className="px-4 py-3">
        <span className="text-sm font-medium text-brand-neutral-950">Follow up questions</span>
      </div>

      {hasQuestions && (
        <ul role="list">
          {questions.map((question, index) => (
            <li key={`${assistantMessageId}_${index}`} className="border-t border-brand-neutral-200" role="none">
              <button
                className="flex w-full items-center gap-2.5 py-2.5 pl-4 pr-2 text-left hover:bg-brand-neutral-100 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-400"
                onClick={() => onQuestionClick(question)}
                type="button"
              >
                <span className="flex-1 text-xs leading-5 text-brand-neutral-800">{question}</span>
                <span aria-hidden className="flex size-9 shrink-0 items-center justify-center">
                  <span className="flex items-center justify-center rounded-full p-1">
                    <ChevronRight className="h-5 w-5 text-brand-neutral-600" />
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {!hasQuestions && followUpQuestionsState.loading && (
        <div className="flex items-center gap-x-1 border-t border-brand-neutral-200 px-4 py-3 text-xs text-brand-neutral-600">
          <InfiniteLoader aria-label="Generating follow up questions" size={16} variant="color" />
          <span>Generating follow up questions...</span>
        </div>
      )}

      {!hasQuestions && followUpQuestionsState.isError && (
        <div className="flex items-center gap-1 border-t border-brand-neutral-200 px-4 py-3 text-xs text-red-800">
          <AlertErrorCircle className="h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>Error generating follow up questions.</span>
        </div>
      )}
    </div>
  )
}
